import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Edit, GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Toggle from '@/components/ui/Toggle';
import Modal from '@/components/ui/Modal';
import DataTableWithQuery from '@/components/ui/DataTableWithQuery';
import ImageUrlUploadField from '@/components/ui/ImageUrlUploadField';
import LocationTargetSelector from '@/components/ads/LocationTargetSelector';
import { userSelectionColumns } from '@/components/users/userSelectionColumns';
import { useAppUsers } from '@/hooks/useUsers';
import type { AdCampaignLocationTarget } from '@/types/ads';
import { MediaSource } from '@/types/media';
import {
  backgroundSlideService,
  type BackgroundSlide,
  type BackgroundSlideTargetMode,
  type CreateBackgroundSlideRequest,
} from '@/services/backgroundSlideService';

interface BackgroundImage {
  id: string;
  type: string;
  title: string;
  year: string;
  genre?: string;
  duration?: string;
  classification?: string;
  isKidsMode: boolean;
  imageUrl: string;
  preview: string;
  displayOrder: number;
  sportsMode: boolean;
  targetMode: BackgroundSlideTargetMode;
  targetUserIds: number[];
  locationTargets: AdCampaignLocationTarget[];
}

const contentTypes = [
  { value: 'estreno', label: 'Estreno' },
  { value: 'proximamente', label: 'Próximamente' },
  { value: 'serie', label: 'Serie' },
  { value: 'documental', label: 'Documental' },
  { value: 'deportes', label: 'Deportes' },
];

const classifications = [
  { value: 'G', label: 'G' },
  { value: 'PG', label: 'PG' },
  { value: 'PG-13', label: 'PG-13' },
  { value: 'R', label: 'R' },
  { value: 'NC-17', label: 'NC-17' },
];

const getTargetModeText = (image: BackgroundImage) => {
  const mode = image.targetMode ?? 'ALL';
  if (mode === 'USERS') {
    return `Usuarios (${image.targetUserIds?.length ?? 0})`;
  }
  if (mode === 'LOCATION') {
    return `Ubicaciones (${image.locationTargets?.length ?? 0})`;
  }
  return 'Todos';
};

const isVideoUrl = (url?: string) => {
  if (!url) return false;
  return /\.(mp4|webm|ogg|mov|m4v|m3u8)(\?|#|$)/i.test(url);
};

const BackgroundImagesTab = () => {
  const [showImageModal, setShowImageModal] = useState(false);
  const [activeModalTab, setActiveModalTab] = useState<'details' | 'location'>('details');
  const [editingImage, setEditingImage] = useState<BackgroundImage | null>(null);
  const [backgroundImages, setBackgroundImages] = useState<BackgroundImage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [activeId, setActiveId] = useState<string | null>(null); // reserved for DragOverlay usage
  const [overId, setOverId] = useState<string | null>(null);

  const [imageForm, setImageForm] = useState({
    imageUrl: '',
    type: '',
    title: '',
    year: '',
    genre: '',
    duration: '',
    classification: '',
    isKidsMode: false,
    sportsMode: false,
    targetMode: 'ALL' as BackgroundSlideTargetMode,
    targetUserIds: [] as string[],
    locationTargets: [] as AdCampaignLocationTarget[],
  });

  useEffect(() => {
    loadBackgroundSlides();
  }, []);

  const orderedImages = useMemo(() => {
    return [...backgroundImages].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  }, [backgroundImages]);

  const isSaveDisabled = useMemo(() => {
    if (isLoading) {
      return true;
    }
    if (imageForm.targetMode === 'USERS') {
      return imageForm.targetUserIds.length === 0;
    }
    if (imageForm.targetMode === 'LOCATION') {
      return imageForm.locationTargets.length === 0;
    }
    return false;
  }, [imageForm.locationTargets.length, imageForm.targetMode, imageForm.targetUserIds.length, isLoading]);

  const loadBackgroundSlides = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await backgroundSlideService.getBackgroundSlides({ _page: 0, _size: 100 });
      const transformedSlides: BackgroundImage[] = response.content.map((slide: BackgroundSlide, idx) => ({
        id: slide.id,
        type: slide.type,
        title: slide.title,
        year: slide.year,
        genre: slide.genre,
        duration: slide.duration,
        classification: slide.classification,
        isKidsMode: slide.isKidsMode,
        sportsMode: slide.sportsMode,
        targetMode: slide.targetMode ?? 'ALL',
        targetUserIds: slide.targetUserIds ?? [],
        locationTargets: slide.locationTargets ?? [],
        imageUrl: slide.imageUrl,
        preview: slide.imageUrl,
        displayOrder: slide.displayOrder ?? idx + 1,
      }));
      // Ensure consistent ordering
      transformedSlides.sort((a, b) => a.displayOrder - b.displayOrder);
      setBackgroundImages(transformedSlides);
    } catch (err) {
      console.error('Error loading background slides:', err);
      setError(err instanceof Error ? err.message : 'Error loading slides');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenImageModal = (image?: BackgroundImage) => {
    setActiveModalTab('details');
    if (image) {
      setEditingImage(image);
      setImageForm({
        imageUrl: image.imageUrl,
        type: image.type.toLowerCase(),
        title: image.title,
        year: image.year,
        genre: image.genre || '',
        duration: image.duration || '',
        classification: image.classification || '',
        isKidsMode: image.isKidsMode,
        sportsMode: image.sportsMode,
        targetMode: image.targetMode ?? 'ALL',
        targetUserIds: (image.targetUserIds ?? []).map((id) => String(id)),
        locationTargets: image.locationTargets ?? [],
      });
    } else {
      setEditingImage(null);
      setImageForm({
        imageUrl: '',
        type: '',
        title: '',
        year: '',
        genre: '',
        duration: '',
        classification: '',
        isKidsMode: false,
        sportsMode: false,
        targetMode: 'ALL',
        targetUserIds: [],
        locationTargets: [],
      });
    }
    setShowImageModal(true);
  };

  const handleSaveImage = async () => {
    const normalizedTargetMode: BackgroundSlideTargetMode = imageForm.targetMode ?? 'ALL';
    const selectedUserIds = imageForm.targetUserIds
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));

    if (normalizedTargetMode === 'USERS' && selectedUserIds.length === 0) {
      setError('Debe seleccionar al menos un usuario cuando el alcance es por usuarios.');
      return;
    }

    if (normalizedTargetMode === 'LOCATION' && imageForm.locationTargets.length === 0) {
      setError('Debe seleccionar al menos una ubicación cuando el alcance es por ubicación.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const slideData: CreateBackgroundSlideRequest = {
        imageUrl: imageForm.imageUrl,
        type: contentTypes.find((t) => t.value === imageForm.type)?.label || imageForm.type,
        title: imageForm.title,
        year: imageForm.year,
        genre: imageForm.genre || undefined,
        duration: imageForm.duration || undefined,
        classification: imageForm.classification || undefined,
        isKidsMode: imageForm.isKidsMode,
        sportsMode: imageForm.sportsMode,
        targetMode: normalizedTargetMode,
        targetUserIds: normalizedTargetMode === 'USERS' ? selectedUserIds : [],
        locationTargets: normalizedTargetMode === 'LOCATION' ? imageForm.locationTargets : [],
        active: true,
      };

      if (editingImage) {
        await backgroundSlideService.updateBackgroundSlide(editingImage.id, slideData);
      } else {
        // New slide gets appended to the end
        const maxOrder = backgroundImages.reduce((max, s) => Math.max(max, s.displayOrder), 0);
        slideData.displayOrder = maxOrder + 1;
        await backgroundSlideService.createBackgroundSlide(slideData);
      }

      await loadBackgroundSlides();
      setShowImageModal(false);
      setActiveModalTab('details');
    } catch (err) {
      console.error('Error saving background slide:', err);
      setError(err instanceof Error ? err.message : 'Error saving slide');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteImage = async (id: string) => {
    if (!confirm('¿Está seguro de eliminar esta imagen?')) return;
    try {
      setIsLoading(true);
      setError(null);
      await backgroundSlideService.deleteBackgroundSlide(id);
      await loadBackgroundSlides();
    } catch (err) {
      console.error('Error deleting background slide:', err);
      setError(err instanceof Error ? err.message : 'Error deleting slide');
    } finally {
      setIsLoading(false);
    }
  };

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (event: DragOverEvent) => {
    setOverId((event.over?.id as string) || null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setOverId(null);
    setActiveId(null);
    if (!over || active.id === over.id) return;

    const current = [...orderedImages];
    const oldIndex = current.findIndex((i) => i.id === active.id);
    const newIndex = current.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const movedList = arrayMove(current, oldIndex, newIndex).map((s, idx) => ({
      ...s,
      displayOrder: idx + 1,
    }));
    setBackgroundImages(movedList);

    try {
      await Promise.all(
        movedList.map((s) => backgroundSlideService.updateDisplayOrder(s.id, s.displayOrder))
      );
    } catch (err) {
      console.error('Error updating display order:', err);
      setError(err instanceof Error ? err.message : 'Error updating display order');
      await loadBackgroundSlides();
    }
  };

  // Sortable row component
  const SortableRow = ({ image }: { image: BackgroundImage }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: image.id });
    const style: React.CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.6 : 1,
    };

    return (
      <tr ref={setNodeRef} style={style} className="hover:bg-gray-50">
        <td className="px-3 py-4 text-gray-400 cursor-grab align-middle" {...attributes} {...listeners}>
          <GripVertical size={18} />
        </td>
        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 align-middle">{image.displayOrder}</td>
        <td className="px-6 py-4 align-middle">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">{image.type}</span>
              {image.isKidsMode && (
                <span className="inline-flex px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">Niños</span>
              )}
            </div>
            <div className="text-sm font-medium text-gray-900">{image.title}</div>
            <div className="text-sm text-gray-500">
              {image.sportsMode && (
                <span className="inline-flex px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded align-middle mr-2">
                  Deportes
                </span>
              )}
              {image.year} • {image.genre} • {image.duration} min • {image.classification}
            </div>
            <div className="text-sm text-gray-500">
              Segmentación: <span className="font-medium text-gray-700">{getTargetModeText(image)}</span>
            </div>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap align-middle">
          {isVideoUrl(image.preview) ? (
            <video
              src={image.preview}
              className="w-20 h-12 object-cover rounded border bg-black"
              muted
              preload="metadata"
            />
          ) : (
            <img src={image.preview} alt={image.title} className="w-20 h-12 object-cover rounded border" />
          )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap align-middle">
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => handleOpenImageModal(image)}>
              <Edit size={14} />
            </Button>
            <Button variant="danger" size="sm" onClick={() => handleDeleteImage(image.id)}>
              <Trash2 size={14} />
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  const activeImage = useMemo(() => backgroundImages.find(i => i.id === activeId) || null, [activeId, backgroundImages]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-gray-800">Agregar Imágenes</h2>
        <Button onClick={() => handleOpenImageModal()} className="flex items-center gap-2" disabled={isLoading}>
          <Plus size={16} />
          Nueva Imagen
        </Button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>}

      {isLoading && (
        <div className="text-center py-4">
          <span className="text-gray-600">Cargando...</span>
        </div>
      )}

      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10" />
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">#</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Información</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vista Previa</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Opciones</th>
              </tr>
            </thead>
            <SortableContext items={orderedImages.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              <tbody className="bg-white divide-y divide-gray-200">
                {orderedImages.map((image) => (
                  <>
                    {overId === image.id && (
                      <tr key={`${image.id}-placeholder`}>
                        <td colSpan={5} className="px-6">
                          <div className="h-4 bg-indigo-100 rounded border-2 border-dashed border-indigo-300 transition-colors hover:bg-indigo-200" />
                        </td>
                      </tr>
                    )}
                    <SortableRow key={image.id} image={image} />
                  </>
                ))}
              </tbody>
            </SortableContext>
          </table>
          <DragOverlay>
            {activeImage ? (
              <div className="flex items-center gap-3 bg-white shadow rounded px-3 py-2 border">
                <GripVertical size={16} className="text-gray-400" />
                <span className="text-sm text-gray-800 font-medium truncate max-w-[240px]">{activeImage.title}</span>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Image Modal */}
      <Modal isOpen={showImageModal} onClose={() => setShowImageModal(false)} title="IMAGEN" size="xl">
        <div className="space-y-4">
          <div className="overflow-hidden bg-white shadow">
            <div className="flex overflow-x-auto" aria-label="Background image modal sections">
              <button
                type="button"
                onClick={() => setActiveModalTab('details')}
                className={`flex flex-1 items-center justify-center px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeModalTab === 'details'
                    ? 'border-indigo-500 text-indigo-600 bg-indigo-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                Detalles
              </button>
              <button
                type="button"
                onClick={() => setActiveModalTab('location')}
                className={`flex flex-1 items-center justify-center px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeModalTab === 'location'
                    ? 'border-indigo-500 text-indigo-600 bg-indigo-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                Segmentación
              </button>
            </div>
          </div>

          {activeModalTab === 'details' && (
            <div className="space-y-4">
              <ImageUrlUploadField
                label="Enlace de imagen/video"
                value={imageForm.imageUrl}
                onChange={(url) => setImageForm((prev) => ({ ...prev, imageUrl: url }))}
                placeholder="https://ejemplo.com/imagen-o-video"
                source={MediaSource.HOME_SLIDE}
                mediaType="both"
                previewClassName="w-full h-auto max-h-80 object-contain rounded-md border border-gray-200 bg-gray-50"
                previewContainerClassName="w-full min-h-40 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center bg-gray-50"
              />

              <Input label="Título (Opcional)" value={imageForm.title} onChange={(e) => setImageForm((prev) => ({ ...prev, title: e.target.value }))} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Tipo de contenido (Opcional)"
                  options={contentTypes}
                  value={imageForm.type}
                  onChange={(e) => setImageForm((prev) => ({ ...prev, type: e.target.value }))}
                  placeholder="-- Seleccionar tipo --"
                  allowEmpty
                />
                <Input label="Género (Opcional)" value={imageForm.genre} onChange={(e) => setImageForm((prev) => ({ ...prev, genre: e.target.value }))} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input label="Año (Opcional)" type="number" value={imageForm.year} onChange={(e) => setImageForm((prev) => ({ ...prev, year: e.target.value }))} />
                <Input
                  label="Duración (Opcional)"
                  type="number"
                  value={imageForm.duration}
                  onChange={(e) => setImageForm((prev) => ({ ...prev, duration: e.target.value }))}
                  placeholder="Minutos"
                />
                <Select
                  label="Clasificación (Opcional)"
                  options={classifications}
                  value={imageForm.classification}
                  onChange={(e) => setImageForm((prev) => ({ ...prev, classification: e.target.value }))}
                  placeholder="Seleccionar clasificación"
                  allowEmpty
                />
              </div>

              <div className="flex items-center gap-3">
                <Toggle checked={imageForm.isKidsMode} onChange={(checked) => setImageForm((prev) => ({ ...prev, isKidsMode: checked }))} />
                <label className="text-sm font-medium text-gray-700">Modo Niño</label>
              </div>

              <div className="flex items-center gap-3">
                <Toggle checked={imageForm.sportsMode} onChange={(checked) => setImageForm((prev) => ({ ...prev, sportsMode: checked }))} />
                <label className="text-sm font-medium text-gray-700">Modo Deportes</label>
              </div>
            </div>
          )}

          {activeModalTab === 'location' && (
            <div className="space-y-4">
              <div className="space-y-3">
                <label className="text-sm font-medium text-gray-700">Alcance</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {[
                    { value: 'ALL' as const, label: 'Todos' },
                    { value: 'USERS' as const, label: 'Usuarios específicos' },
                    { value: 'LOCATION' as const, label: 'Ubicación o Grupo' },
                  ].map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer ${
                        imageForm.targetMode === option.value
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-gray-200 text-gray-700'
                      }`}
                    >
                      <input
                        type="radio"
                        name="backgroundImageTargetMode"
                        value={option.value}
                        checked={imageForm.targetMode === option.value}
                        onChange={() => {
                          setImageForm((prev) => ({
                            ...prev,
                            targetMode: option.value,
                            targetUserIds: [],
                            locationTargets: [],
                          }));
                        }}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>

              {imageForm.targetMode === 'ALL' && (
                <p className="text-sm text-gray-500">
                  La imagen se mostrará a todos los usuarios.
                </p>
              )}

              {imageForm.targetMode === 'USERS' && (
                <div className="space-y-2">
                  <div className="text-sm text-gray-500">
                    Seleccionados: <span className="font-semibold text-gray-900">{imageForm.targetUserIds.length}</span>
                  </div>
                  <DataTableWithQuery
                    columns={userSelectionColumns}
                    actions={[]}
                    useQueryHook={useAppUsers}
                    itemsPerPage={5}
                    title=""
                    searchPlaceholder="Buscar por nombre o usuario..."
                    tableStyle="compact"
                    enableRowSelection
                    selectedRowIds={imageForm.targetUserIds}
                    onSelectionChange={(targetUserIds) => setImageForm((prev) => ({ ...prev, targetUserIds }))}
                    getRowId={(user) => String(user.id)}
                    className="shadow-none border border-gray-200"
                  />
                </div>
              )}

              {imageForm.targetMode === 'LOCATION' && (
                <LocationTargetSelector
                  value={imageForm.locationTargets}
                  onChange={(next) => setImageForm((prev) => ({ ...prev, locationTargets: next }))}
                />
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setShowImageModal(false)} disabled={isLoading}>
              Cancelar
            </Button>
            <Button onClick={handleSaveImage} disabled={isSaveDisabled}>
              {isLoading ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BackgroundImagesTab;
